/**
 * CustomerApprovalSection.jsx
 * 
 * Sezione approvazioni per il cliente in "Il Mio Viaggio".
 * Design pulito, naturale, non burocratico.
 * 
 * UX Principles:
 * - Card chiare per ogni item da approvare
 * - Contesto leggibile (cosa, dove, quando)
 * - Azioni evidenti ma non aggressive
 * - Feedback immediato dopo approvazione
 * - Progressive disclosure
 */

import React, { useState } from 'react';
import { 
  Check, X, Clock, ChevronDown, ChevronUp,
  MapPin, Calendar, Info, CheckCircle, XCircle,
  Send, AlertTriangle, HelpCircle
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

// ============== APPROVAL CARD ==============

/**
 * Card singola per approvazione item.
 * Design rassicurante e chiaro.
 */
function ApprovalCard({ 
  approval, 
  onApprove, 
  onReject, 
  isVerified,
  onRequestVerification,
  submitting 
}) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState('');
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  
  const item = approval.item_details || {};
  const statusDisplay = approval.status_display || {};
  
  // Type icons
  const typeIcons = {
    accommodation: '🏠',
    restaurant: '🍽️',
    activity: '🎯',
    experience: '✨',
    transport: '🚗',
    other: '📍'
  };
  
  const typeIcon = typeIcons[item.type] || typeIcons.other;
  
  return (
    <Card 
      className="overflow-hidden border-amber-200 bg-gradient-to-br from-amber-50/50 to-white"
      data-testid={`approval-card-${approval.id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          {/* Type Icon */}
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-lg flex-shrink-0">
            {typeIcon}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-medium text-umbria-charcoal text-sm">
                  {approval.item_title || 'Elemento'}
                </h3>
                <div className="flex flex-wrap items-center gap-2 text-xs text-umbria-muted mt-1">
                  {item.day && (
                    <span className="flex items-center gap-1">
                      <Calendar size={10} />
                      Giorno {item.day}
                    </span>
                  )}
                  {item.time && (
                    <span>{item.time}</span>
                  )}
                  {item.location && (
                    <span className="flex items-center gap-1">
                      <MapPin size={10} />
                      {item.location}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Status Badge */}
              <Badge className={`${statusDisplay.color} text-[10px] flex-shrink-0`}>
                <Clock size={10} className="mr-1" />
                {statusDisplay.label}
              </Badge>
            </div>
            
            {/* Context Message */}
            {approval.approval_context && (
              <p className="text-xs text-umbria-muted mt-2 bg-white/60 p-2 rounded-lg border border-amber-100">
                {approval.approval_context}
              </p>
            )}
            
            {/* Expand for details */}
            {item.notes && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-amber-600 hover:text-amber-700 mt-2 flex items-center gap-1"
              >
                {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {expanded ? 'Nascondi dettagli' : 'Mostra dettagli'}
              </button>
            )}
            
            {expanded && item.notes && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                {item.notes}
              </div>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="mt-4 pt-3 border-t border-amber-100">
          {!isVerified ? (
            /* Non verificato */
            <div className="flex items-center gap-2 text-xs text-umbria-muted">
              <Info size={12} className="text-amber-500" />
              <span>Per confermare, </span>
              <button 
                onClick={onRequestVerification}
                className="text-amber-600 hover:underline font-medium"
              >
                verifica il tuo accesso
              </button>
            </div>
          ) : showRejectConfirm ? (
            /* Conferma rifiuto */
            <div className="space-y-2">
              <p className="text-xs text-gray-600">
                Vuoi lasciare una nota? (opzionale)
              </p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Es. Preferisco un altro orario..."
                className="w-full text-xs border rounded-lg p-2 h-16 resize-none"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 text-xs h-8"
                  onClick={() => setShowRejectConfirm(false)}
                  disabled={submitting}
                >
                  Annulla
                </Button>
                <Button
                  size="sm"
                  className="flex-1 text-xs h-8 bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => onReject(approval.id, note)}
                  disabled={submitting}
                >
                  {submitting ? 'Invio...' : 'Conferma rifiuto'}
                </Button>
              </div>
            </div>
          ) : (
            /* Azioni principali */
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-1 text-xs h-9"
                onClick={() => onApprove(approval.id)}
                disabled={submitting}
                data-testid="approve-btn"
              >
                <Check size={14} />
                {submitting ? 'Conferma...' : 'Approva'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-gray-600 hover:text-red-600 hover:border-red-200 gap-1 text-xs h-9"
                onClick={() => setShowRejectConfirm(true)}
                disabled={submitting}
                data-testid="reject-btn"
              >
                <X size={14} />
                Non approvo
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


// ============== STATUS CARDS ==============

/**
 * Card per item già approvato - mostra prossimo passo
 */
function ApprovedItemCard({ item }) {
  return (
    <div 
      className="flex items-center gap-3 p-3 bg-green-50/50 border border-green-100 rounded-lg"
      data-testid={`approved-item-${item.item_id}`}
    >
      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
        <CheckCircle size={16} className="text-green-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-green-800 truncate">
          {item.title}
        </p>
        <p className="text-xs text-green-600">
          {item.routing_label || 'Pronto per l\'invio'}
        </p>
      </div>
    </div>
  );
}

/**
 * Card per item in attesa conferma fornitore
 */
function AwaitingSupplierCard({ item }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-blue-50/50 border border-blue-100 rounded-lg">
      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
        <Send size={14} className="text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-blue-800 truncate">
          {item.title}
        </p>
        <p className="text-xs text-blue-600">
          In attesa di conferma
        </p>
      </div>
    </div>
  );
}


// ============== MAIN SECTION ==============

/**
 * Sezione completa approvazioni per Il Mio Viaggio.
 * Design ergonomico e rassicurante.
 */
export function CustomerApprovalSection({
  pendingApprovals = [],
  approvedItems = [],
  awaitingItems = [],
  isVerified,
  onApprove,
  onReject,
  onRequestVerification,
  submitting,
  loading
}) {
  const hasPending = pendingApprovals.length > 0;
  const hasApproved = approvedItems.length > 0;
  const hasAwaiting = awaitingItems.length > 0;
  
  if (loading) {
    return (
      <Card className="bg-white/80">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Clock className="h-5 w-5 animate-spin text-umbria-olive" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Niente da mostrare
  if (!hasPending && !hasApproved && !hasAwaiting) {
    return null;
  }
  
  return (
    <div className="space-y-4" data-testid="approval-section">
      {/* === PENDING APPROVALS === */}
      {hasPending && (
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock size={12} className="text-amber-600" />
            </div>
            <h3 className="text-sm font-medium text-umbria-charcoal">
              Da confermare
            </h3>
            <Badge className="bg-amber-50 text-amber-700 text-[10px]">
              {pendingApprovals.length}
            </Badge>
          </div>
          
          {/* Info box */}
          <div className="flex items-start gap-2 p-3 bg-amber-50/50 rounded-lg border border-amber-100">
            <Info size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              Abbiamo preparato questi elementi per te. 
              Una volta confermati, procederemo con la prenotazione.
            </p>
          </div>
          
          {/* Cards */}
          <div className="space-y-3">
            {pendingApprovals.map(approval => (
              <ApprovalCard
                key={approval.id}
                approval={approval}
                isVerified={isVerified}
                onApprove={onApprove}
                onReject={onReject}
                onRequestVerification={onRequestVerification}
                submitting={submitting}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* === APPROVED (next step visible) === */}
      {hasApproved && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle size={14} className="text-green-600" />
            <span className="text-xs font-medium text-green-700">
              Confermati da te
            </span>
          </div>
          <div className="space-y-2">
            {approvedItems.slice(0, 3).map(item => (
              <ApprovedItemCard key={item.item_id} item={item} />
            ))}
            {approvedItems.length > 3 && (
              <p className="text-xs text-gray-500 pl-2">
                +{approvedItems.length - 3} altri elementi confermati
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* === AWAITING SUPPLIER === */}
      {hasAwaiting && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Send size={14} className="text-blue-600" />
            <span className="text-xs font-medium text-blue-700">
              In elaborazione
            </span>
          </div>
          <div className="space-y-2">
            {awaitingItems.slice(0, 2).map(item => (
              <AwaitingSupplierCard key={item.item_id} item={item} />
            ))}
            {awaitingItems.length > 2 && (
              <p className="text-xs text-gray-500 pl-2">
                +{awaitingItems.length - 2} in attesa di conferma
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ============== HOOK ==============

export function useCustomerApprovals(journeyCode, sessionToken) {
  const [pending, setPending] = React.useState([]);
  const [routingStatus, setRoutingStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  const fetchData = React.useCallback(async () => {
    try {
      // Fetch pending approvals
      const approvalsRes = await fetch(`${API_URL}/api/journey/${journeyCode}/approvals`);
      if (approvalsRes.ok) {
        const data = await approvalsRes.json();
        setPending(data.pending_approvals || []);
      }
      
      // Fetch routing status
      const routingRes = await fetch(`${API_URL}/api/journey/${journeyCode}/routing-status`);
      if (routingRes.ok) {
        const data = await routingRes.json();
        setRoutingStatus(data);
      }
    } catch (err) {
      console.error('Error fetching approvals:', err);
    } finally {
      setLoading(false);
    }
  }, [journeyCode, API_URL]);
  
  React.useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const respondToApproval = async (approvalId, approved, note = null) => {
    if (!sessionToken) return { success: false, error: 'Non verificato' };
    
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/journey/${journeyCode}/approvals/${approvalId}/respond`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Token': sessionToken
          },
          body: JSON.stringify({ approved, note })
        }
      );
      
      if (res.ok) {
        await fetchData(); // Refresh
        return { success: true };
      } else {
        const err = await res.json();
        return { success: false, error: err.detail };
      }
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setSubmitting(false);
    }
  };
  
  const approve = (approvalId) => respondToApproval(approvalId, true);
  const reject = (approvalId, note) => respondToApproval(approvalId, false, note);
  
  // Categorize items from routing status
  const approvedItems = routingStatus?.items?.filter(
    i => i.approval_status === 'approved' && i.routing_status === 'ready_to_send'
  ) || [];
  
  const awaitingItems = routingStatus?.items?.filter(
    i => ['sent_to_supplier', 'awaiting_reply'].includes(i.routing_status)
  ) || [];
  
  return {
    pendingApprovals: pending,
    approvedItems,
    awaitingItems,
    loading,
    submitting,
    approve,
    reject,
    reload: fetchData
  };
}

export default CustomerApprovalSection;
